import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";

import {
  Search,
  Filter,
  Plus,
  LayoutGrid,
  FolderKanban,
  KanbanSquare,
  ListTodo,
  Bug,
  Clock,
  BarChart3,
  Users,
  Settings,
  X,
  Trash2,
} from "lucide-react";

/* ================= HELPER ================= */
const navigate = (path: string) => {
  window.location.hash = path;
};

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export default function Issues() {
  const { user } = useAuth();
  const [issues, setIssues] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    priority: "",
  });
  const [newIssue, setNewIssue] = useState({
    type: "T",
    summary: "",
    status: "todo",
    priority: "medium",
    assignee: user?.name || "Admin",
    due_date: new Date().toISOString().split("T")[0],
  });

  const fetchIssues = async () => {
    try {
      const res = await fetch(`${API_BASE}/get_issues.php`);
      const data = await res.json();
      setIssues(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Fetch error:", error);
    }
  };

  useEffect(() => {
    fetchIssues();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewIssue((prev) => ({ ...prev, [name]: value }));
  };

  const createIssue = async () => {
    if (!newIssue.summary) return alert("Summary is required!");

    const issueData = {
      ...newIssue,
      keyId: `WEB-${Math.floor(Math.random() * 10000)}`,
    };

    try {
      const res = await fetch(`${API_BASE}/create_issue.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(issueData),
      });

      const data = await res.text();   // 👈 change to text first
      console.log("Create response:", data);

      fetchIssues();
      setIsModalOpen(false);
    } catch (error) {
      console.error("Create error:", error);
    }
  };

  const deleteIssue = async (id: number) => {
    if (!confirm("Are you sure you want to delete this issue?")) return;

    try {
      await fetch(`${API_BASE}/delete_issue.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id }),
      });
      fetchIssues();
    } catch (error) {
      console.error("Delete error:", error);
    }
  };

  // Filter issues based on search query and selected filters
  const filteredIssues = issues.filter((issue) => {
    const matchesSearch =
      (issue.summary || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (issue.keyId || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (issue.assignee || "").toLowerCase().includes(searchQuery.toLowerCase());


    const matchesStatus = filters.status ? issue.status === filters.status : true;
    const matchesPriority = filters.priority ? issue.priority === filters.priority : true;

    return matchesSearch && matchesStatus && matchesPriority;
  });

  const resetFilters = () => {
    setFilters({ status: "", priority: "" });
    setIsFilterOpen(false);
  };

  return (
    <div className="flex flex-col flex-1 bg-[#f6f7fb]">

      {/* ================= SIDEBAR ================= */}
      <div className="flex flex-1 overflow-hidden">
        <aside className="w-64 shrink-0 bg-white border-r px-4 py-5">
          {(user?.role === 'admin' || user?.role === 'hr' || user?.role === 'product_manager' || user?.role === 'scrum_master') && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white py-2 rounded-md mb-6 text-sm font-medium"
            >
              <Plus size={16} /> Create Issue
            </button>
          )}

          <nav className="space-y-1 text-sm">
            <Menu icon={LayoutGrid} label="Main Dashboard" path="dashboard" />

            {(user?.role === 'admin' || user?.role === 'hr' || user?.role === 'product_manager' || user?.role === 'scrum_master') && (
              <>
                <Menu icon={LayoutGrid} label="Jira Dashboard" path="jira" />
                <Menu icon={FolderKanban} label="Projects" path="projects" />
              </>
            )}

            <Menu icon={KanbanSquare} label="Board" path="board" />

            {(user?.role === 'admin' || user?.role === 'hr' || user?.role === 'product_manager' || user?.role === 'scrum_master') && (
              <>
                <Menu icon={ListTodo} label="Backlog" path="backlog" />
                <Menu icon={Bug} label="Issues" path="issues" active />
                <Menu icon={Clock} label="Time Tracking" path="time" />
                <Menu icon={BarChart3} label="Reports" path="reports" />
                <Menu icon={Users} label="Team" path="team" />
                <Menu icon={Settings} label="Settings" path="settings" />
              </>
            )}
          </nav>

          <div className="mt-8">
            <p className="text-xs text-gray-400 mb-2 uppercase">
              Recent Projects
            </p>
            <div className="space-y-2 text-sm">
              <p className="text-green-600">WEB Website Redesign</p>
              <p className="text-gray-600">MOB Mobile App</p>
              <p className="text-gray-600">CRM CRM Enhancement</p>
            </div>
          </div>
        </aside>

        {/* ================= MAIN ================= */}
        <main className="flex-1 p-6 overflow-y-auto relative">
          {/* TOP TABS */}
          <div className="flex gap-3 mb-6 text-sm">
            <Tab label="Dashboard" path="jira" />
            <Tab label="Projects" path="projects" />
            <Tab label="Board" path="board" />
            <Tab label="Backlog" path="backlog" />
            <Tab label="Issues" path="issues" active />
          </div>          {/* HEADER */}
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-xl font-semibold">Issues</h1>
              <p className="text-sm text-gray-500">
                View and manage all issues
              </p>
            </div>

            <div className="flex items-center gap-3 relative">
              <div className="flex items-center gap-2 border px-3 py-2 rounded-md bg-white">
                <Search size={14} />
                <input
                  type="text"
                  placeholder="Search issues..."
                  className="outline-none text-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className={`flex items-center gap-2 border px-3 py-2 rounded-md text-sm ${filters.status || filters.priority ? "bg-green-100 border-green-500 text-green-700" : "bg-white"
                  }`}
              >
                <Filter size={14} /> Filter
              </button>

              {/* Filter Dropdown */}
              {isFilterOpen && (
                <div className="absolute top-10 right-32 bg-white border rounded-lg shadow-lg w-64 p-4 z-10">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-semibold">Filter Issues</h3>
                    <button onClick={() => setIsFilterOpen(false)}><X size={14} /></button>
                  </div>

                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                    <select
                      className="w-full border rounded p-1.5 text-sm"
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    >
                      <option value="">All Statuses</option>
                      <option value="todo">To Do</option>
                      <option value="in progress">In Progress</option>
                      <option value="backlog">Backlog</option>
                      <option value="done">Done</option>
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Priority</label>
                    <select
                      className="w-full border rounded p-1.5 text-sm"
                      value={filters.priority}
                      onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                    >
                      <option value="">All Priorities</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="highest">Highest</option>
                    </select>
                  </div>

                  <button
                    onClick={resetFilters}
                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm py-1.5 rounded"
                  >
                    Clear Filters
                  </button>
                </div>
              )}

              <button
                onClick={() => setIsModalOpen(true)}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md text-sm"
              >
                <Plus size={14} /> Create Issue
              </button>
            </div>
          </div>

          {/* ================= TABLE ================= */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left">TYPE</th>
                  <th className="px-4 py-3 text-left">KEY</th>
                  <th className="px-4 py-3 text-left">SUMMARY</th>
                  <th className="px-4 py-3 text-left">STATUS</th>
                  <th className="px-4 py-3 text-left">PRIORITY</th>
                  <th className="px-4 py-3 text-left">ASSIGNEE</th>
                  <th className="px-4 py-3 text-left">DUE DATE</th>
                  <th className="px-4 py-3 text-right">ACTIONS</th>
                </tr>
              </thead>

              <tbody>
                {filteredIssues.length > 0 ? (
                  filteredIssues.map((issue) => (
                    <IssueRow
                      key={issue.id}
                      id={issue.id}
                      type={issue.type}
                      keyId={`WEB-${issue.id}`}
                      summary={issue.summary}
                      status={issue.status}
                      priority={issue.priority}
                      assignee={issue.assignee}
                      date={issue.due_date}
                      onDelete={deleteIssue}
                    />

                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="text-center py-4 text-gray-500">
                      No issues found matching your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ================= CREATE ISSUE MODAL ================= */}
          {isModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
              <div className="bg-white p-6 rounded-lg w-96 shadow-lg relative">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="absolute top-3 right-3 text-gray-500 hover:text-black"
                >
                  <X size={18} />
                </button>
                <h2 className="text-lg font-semibold mb-4">Create New Issue</h2>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Type</label>
                    <select
                      name="type"
                      value={newIssue.type}
                      onChange={handleInputChange}
                      className="w-full border rounded p-2 text-sm"
                    >
                      <option value="T">Task</option>
                      <option value="B">Bug</option>
                      <option value="S">Story</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Summary</label>
                    <input
                      type="text"
                      name="summary"
                      value={newIssue.summary}
                      onChange={handleInputChange}
                      className="w-full border rounded p-2 text-sm"
                      placeholder="Issue summary"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <select
                      name="status"
                      value={newIssue.status}
                      onChange={handleInputChange}
                      className="w-full border rounded p-2 text-sm"
                    >
                      <option value="todo">To Do</option>
                      <option value="in progress">In Progress</option>
                      <option value="backlog">Backlog</option>
                      <option value="done">Done</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Priority</label>
                    <select
                      name="priority"
                      value={newIssue.priority}
                      onChange={handleInputChange}
                      className="w-full border rounded p-2 text-sm"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="highest">Highest</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Assignee</label>
                    <input
                      type="text"
                      name="assignee"
                      value={newIssue.assignee}
                      onChange={handleInputChange}
                      className="w-full border rounded p-2 text-sm"
                      placeholder="Assignee name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Due Date</label>
                    <input
                      type="date"
                      name="due_date"
                      value={newIssue.due_date}
                      onChange={handleInputChange}
                      className="w-full border rounded p-2 text-sm"
                    />
                  </div>

                  <div className="flex justify-end gap-2 mt-4">
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="px-4 py-2 bg-gray-200 rounded text-sm hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={createIssue}
                      className="px-4 py-2 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                    >
                      Create
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>


    </div>
  );

  /* ================= COMPONENTS ================= */

  function Menu({ label, path, active, icon: Icon }: any) {
    return (
      <button
        onClick={() => navigate(path)}
        className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition
      ${active
            ? "bg-green-100 text-green-700 font-medium"
            : "text-gray-700 hover:bg-gray-100"
          }`}
      >
        <Icon size={16} />
        <span>{label}</span>
      </button>
    );
  }

  function Tab({ label, path, active }: any) {
    return (
      <button
        onClick={() => navigate(path)}
        className={`px-3 py-1.5 rounded-md text-sm transition
      ${active
            ? "bg-green-500 text-white"
            : "bg-white border text-gray-700 hover:bg-gray-100"
          }`}
      >
        {label}
      </button>
    );
  }

  function IssueRow({ id, type, keyId, summary, status, priority, assignee, date, onDelete }: any) {
    const statusColor: any = {
      "in progress": "bg-blue-100 text-blue-600",
      todo: "bg-gray-100 text-gray-600",
      backlog: "bg-gray-200 text-gray-600",
      done: "bg-green-100 text-green-600",
    };

    const priorityColor: any = {
      low: "bg-blue-100 text-blue-600",
      medium: "bg-yellow-100 text-yellow-700",
      high: "bg-orange-100 text-orange-700",
      highest: "bg-red-100 text-red-700",
    };

    return (
      <tr className="border-t hover:bg-gray-50 transition-colors">
        <td className="px-4 py-3">{type}</td>
        <td className="px-4 py-3 text-gray-500">{keyId}</td>
        <td className="px-4 py-3">{summary}</td>
        <td className="px-4 py-3">
          <span className={`text-xs px-2 py-0.5 rounded ${statusColor[status] || "bg-gray-100 text-gray-600"}`}>
            {status}
          </span>
        </td>
        <td className="px-4 py-3">
          <span className={`text-xs px-2 py-0.5 rounded ${priorityColor[priority] || "bg-gray-100 text-gray-600"}`}>
            {priority}
          </span>
        </td>
        <td className="px-4 py-3">
          <span className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">
              {assignee.charAt(0).toUpperCase()}
            </span>
            {assignee}
          </span>
        </td>
        <td className="px-4 py-3 text-gray-500">{date}</td>
        <td className="px-4 py-3 text-right">
        </td>
      </tr>
    );
  }
}